/**
 * Analytics home (screenshots/82; brief §2.13 Home; phone layout 91): title + explainer, search, Create dashboard; the
 * All · Starred · Yours · Team · Shared with you switch with counts and the Sort menu; "Starred · in your sidebar" first
 * (in the sidebar's order), then all dashboards, ending with the dashed Create dashboard card.
 * Starring goes through navStore, so the sidebar updates at once (with an Undo toast). The card ⋯ menu: Duplicate,
 * Rename (editors), Delete… (owner/admin, with a confirm). From the handoff starter's src/analytics/AnalyticsHome.tsx.
 */
import { useEffect, useRef, useState } from "react";
import { Copy, Ellipsis, Link2, Lock, Pencil, Plus, Search, Star, Trash2, Users, ChevronDown } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { ViewSwitch } from "../components/shared/ui";
import { navigate, plainClick } from "../app/router";
import { navStore, useNav } from "../nav/navStore";
import { accessLabel, canEdit, canShare, editedLine, listDashboards, type Dashboard, type ListFilter, type Viewer } from "./dashboards";
import { analyticsStore, useAnalytics } from "./analyticsStore";
import { CreateDashboardDialog } from "./CreateDashboardDialog";
import { Thumb } from "./Thumb";
import { toastStore } from "../stores/toastStore";

export type CardAction = "duplicate" | "rename" | "delete";
const ACCESS_ICON = { "Only you": Lock, Team: Users, Shared: Users, Link: Link2 } as const;
const failed = (e: unknown) => toastStore.getState().show({ message: (e as Error).message || "Something went wrong" });

export function AnalyticsHome() {
  const snap = useAnalytics((s) => s.snapshot);
  const dashboards = useAnalytics((s) => s.dashboards);
  const starred = useNav((s) => s.prefs.starred);
  const [f, setF] = useState<ListFilter>("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"edited" | "title">("edited");
  const [creating, setCreating] = useState(() => typeof location !== "undefined" && new URLSearchParams(location.search).has("create"));
  if (!snap) return null;
  const viewer: Viewer = snap.me;
  const { list, counts, starred: starredList } = listDashboards(dashboards, viewer, starred, f, q, sort);
  const rest = f === "all" ? list.filter((d) => !starred.includes(d.id)) : list;
  const onAction = (kind: CardAction, d: Dashboard, title?: string) => {
    const s = analyticsStore.getState();
    if (kind === "duplicate") void s.duplicate(d.id).catch(failed);
    if (kind === "rename" && title) void s.saveMeta(d.id, { title }).catch(failed);
    if (kind === "delete") void s.remove(d.id).then(() => { if (navStore.getState().prefs.starred.includes(d.id)) navStore.getState().toggleStar(d.id, d.title); }).catch(failed);
  };

  return (
    <div className="an-home">
      <header className="an-head">
        <div className="an-head__title">
          <h1 className="an-title">Analytics</h1>
        </div>
        <div className="an-head__actions">
          <label className="an-search">
            <Search size={14} aria-hidden />
            <input type="search" placeholder="Search dashboards" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search dashboards" />
          </label>
          <button className="btn btn--primary an-create" onClick={() => setCreating(true)}><Plus size={14} aria-hidden /><span className="an-create__long">Create dashboard</span><span className="an-create__short">Create</span></button>
        </div>
      </header>
      <div className="an-filters">
        <ViewSwitch label="Show" value={f} onChange={setF} options={[
          { id: "all", label: `All ${counts.all}` }, { id: "starred", label: `Starred ${counts.starred}` }, { id: "yours", label: `Yours ${counts.yours}` },
          { id: "team", label: `Team ${counts.team}` }, { id: "shared", label: `Shared with you ${counts.shared}` }]} />
        <label className="an-sort">Sort
          <Dropdown className="an-sort__pill" label="Sort" align="end" value={sort} onChange={setSort} chevron={<ChevronDown size={14} aria-hidden />}
            options={[{ value: "edited" as const, label: "Last edited" }, { value: "title" as const, label: "Name" }]} />
        </label>
      </div>
      {f === "all" && starredList.length > 0 && <Section title="Starred · in your sidebar" mobileTitle="Starred · in More" items={starredList} starred={starred} viewer={viewer} onAction={onAction} />}
      <Section title={f === "all" ? "All dashboards" : ""} items={rest} starred={starred} viewer={viewer} onAction={onAction} create={() => setCreating(true)} />
      {list.length === 0 && (q || f !== "all") && <p className="an-empty">No dashboards match. <button className="link" onClick={() => { setQ(""); setF("all"); }}>Clear search</button></p>}
      {creating && <CreateDashboardDialog viewer={viewer} orgName={snap.me.orgName} onClose={() => setCreating(false)} />}
    </div>
  );
}

function Section({ title, mobileTitle, items, starred, create, viewer, onAction }: {
  title: string; mobileTitle?: string; items: Dashboard[]; starred: string[]; create?: () => void; viewer: Viewer; onAction: (kind: CardAction, d: Dashboard, title?: string) => void;
}) {
  if (!items.length && !create) return null;
  return (
    <section className="an-section">
      {title && <h2 className="eyebrow an-section__label"><span className="an-desk">{title}</span><span className="an-phone">{mobileTitle ?? title}</span></h2>}
      <ul className="an-cards">
        {items.map((d) => <Card key={d.id} d={d} on={starred.includes(d.id)} viewer={viewer} onAction={onAction} />)}
        {create && (
          <li className="an-cards__create">
            <button className="an-card an-card--create" onClick={create}>
              <span className="an-card__plus"><Plus size={16} aria-hidden /></span>
              <strong>Create dashboard</strong>
              <small>Blank, from a template, or a copy of your Overview</small>
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}

function Card({ d, on, viewer, onAction }: { d: Dashboard; on: boolean; viewer: Viewer; onAction: (kind: CardAction, d: Dashboard, title?: string) => void }) {
  const access = accessLabel(d);
  const AccessIcon = ACCESS_ICON[access];
  const href = `/dashboard/analytics/${d.id}`;
  const open = (e: React.MouseEvent) => { if (!plainClick(e)) return; e.preventDefault(); navigate(href); };
  return (
    <li className="an-card">
      <a href={href} className="an-card__link" onClick={open} aria-label={d.title}>
        <Thumb layout={d.layout} className="an-card__thumb" />
        <Thumb layout={d.layout} col={5} row={3.4} gap={1.5} className="an-card__thumb--phone" />
      </a>
      <div className="an-card__body">
        <div className="an-card__row">
          <a href={href} className="an-card__title" onClick={open}>{d.title}</a>
          <button className={`an-star${on ? " is-on" : ""}`} aria-pressed={on} data-tip={on ? "Unstar · remove from sidebar" : "Star · add to sidebar"}
            aria-label={on ? `Unstar ${d.title}` : `Star ${d.title}`} onClick={() => navStore.getState().toggleStar(d.id, d.title)}><Star size={16} aria-hidden /></button>
          <CardMenu d={d} canDelete={canShare(d, viewer)} canRename={canEdit(d, viewer)} onAction={onAction} />
        </div>
        <p className="an-card__meta">
          <span className="an-chip"><AccessIcon size={13} aria-hidden />{access}</span>
          <span className="an-desk">{d.layout.length} widgets · {editedLine(d, viewer.userId)}</span>
          <span className="an-phone">{access} · {d.layout.length} widgets</span>
        </p>
      </div>
    </li>
  );
}

/** The ⋯ menu on a card and on the dashboard header: Duplicate, Rename (editors), Delete… (owner/admin, with a confirm). */
export function CardMenu({ d, canDelete, canRename, onAction, icon }: { d: Dashboard; canDelete: boolean; canRename: boolean; onAction: (kind: CardAction, d: Dashboard, title?: string) => void; icon?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"menu" | "rename" | "delete">("menu");
  const [title, setTitle] = useState(d.title);
  const box = useRef<HTMLSpanElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const close = (focus = true) => { setOpen(false); setMode("menu"); if (focus) btn.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    box.current?.querySelector<HTMLElement>("[role=menuitem], input, button.btn")?.focus();
    const away = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) close(false); };
    addEventListener("pointerdown", away, true);
    return () => removeEventListener("pointerdown", away, true);
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span className="an-card__menu" ref={box} onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}>
      <button ref={btn} className="an-icon-btn" aria-label={`More for ${d.title}`} aria-haspopup="menu" aria-expanded={open} onClick={() => (open ? close() : setOpen(true))}>{icon ?? <Ellipsis size={16} aria-hidden />}</button>
      {open && mode === "menu" && (
        <ul className="widget-menu ov-menu an-menu" role="menu" aria-label={`${d.title} actions`}>
          <li role="none"><button role="menuitem" onClick={() => { onAction("duplicate", d); close(); }}><Copy size={15} aria-hidden />Duplicate</button></li>
          {canRename && <li role="none"><button role="menuitem" onClick={() => setMode("rename")}><Pencil size={15} aria-hidden />Rename</button></li>}
          {canDelete && <li role="none"><button role="menuitem" className="is-danger" onClick={() => setMode("delete")}><Trash2 size={15} aria-hidden />Delete…</button></li>}
        </ul>
      )}
      {open && mode === "rename" && (
        <form className="widget-menu an-menu an-menu--form" onSubmit={(e) => { e.preventDefault(); if (title.trim()) { onAction("rename", d, title.trim()); close(); } }}>
          <label className="an-field"><span>Name</span><input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} /></label>
          <button className="btn btn--primary" type="submit" disabled={!title.trim()}>Save</button>
        </form>
      )}
      {open && mode === "delete" && (
        <div className="widget-menu an-menu an-menu--form" role="alertdialog" aria-label={`Delete ${d.title}?`}>
          <p><strong>Delete “{d.title}”?</strong> It’s removed for everyone{d.link.mode !== "off" ? " and its link stops working" : ""}. Stars in other people’s sidebars disappear.</p>
          <span className="an-menu__btns">
            <button className="btn btn--secondary" onClick={() => close()}>Cancel</button>
            <button className="btn btn--danger" onClick={() => { onAction("delete", d); close(false); }}>Delete dashboard</button>
          </span>
        </div>
      )}
    </span>
  );
}
